import React, { useState, useEffect, useRef, useCallback } from "react";
import { useSelector } from "react-redux";

import {
  Box,
  TextField,
  Button,
  Typography,
  Divider,
  Snackbar,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";

import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import TranslateIcon from "@mui/icons-material/Translate";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  createArticle,
  getArticleById,
  GetAttachments,
  DownloadAttachment,
  DeleteFileAttachment,
  updateArticle,
} from "../api/memoHebdo.api";

// ================= ICONS =================

import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import DeleteIcon from "@mui/icons-material/Delete";
import DownloadIcon from "@mui/icons-material/Download";
import LanguageIcon from "@mui/icons-material/Language";

function ArticleForm({ isEdit = false, token }) {
  const navigate = useNavigate();
  const { t } = useTranslation(["memohebdo"]);
  const { id } = useParams();

  const userState = useSelector((state) => state.user);

  // ================= REFS =================

  const fileInputRef = useRef(null);

  // ================= STATE =================

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const [fileToDelete, setFileToDelete] = useState(null);

  const [languageDialogOpen, setLanguageDialogOpen] = useState(false);

  const [selectedLanguage, setSelectedLanguage] = useState("");

  const [form, setForm] = useState({
    titleFr: "",
    textFr: "",
    titleEn: "",
    textEn: "",
  });

  // ================= LOADING =================

  const [loading, setLoading] = useState(false);

  // ================= FILES =================

  const [files, setFiles] = useState([]);

  // ================= EXISTING FILES =================

  const [existingFiles, setExistingFiles] = useState([]);

  // ================= SNACKBAR =================

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // ================= LOAD ARTICLE =================

  useEffect(() => {
    const loadArticle = async () => {
      if (!isEdit || !id) return;

      try {
        setLoading(true);

        const data = await getArticleById(userState.token, id);

        console.log("ARTICLE DATA:", data);

        setForm((prev) => ({
          ...prev,
          titleFr: data.titleFr ?? "",
          textFr: data.textFr ?? "",
          titleEn: data.titleEn ?? "",
          textEn: data.textEn ?? "",
        }));
        console.log("FORM AFTER SET:", {
          titleFr: data.titleFr,
          titleEn: data.titleEn,
          textFr: data.textFr,
          textEn: data.textEn,
        });
        // ================= LOAD ATTACHMENTS =================

        try {
          const attachments = await GetAttachments(userState.token, id);

          console.log("ATTACHMENTS:", attachments);

          setExistingFiles(attachments || []);
        } catch (err) {
          console.error("Attachment error:", err);

          setExistingFiles([]);
        }
      } catch (err) {
        console.error(err);

        setSnackbar({
          open: true,
          message: err.message || "Error loading article.",
          severity: "error",
        });
      } finally {
        setLoading(false);
      }
    };

    loadArticle();
  }, [id, isEdit, userState.token]);

  // ================= HANDLE INPUT =================
  const handleTextFrChange = useCallback((value) => {
    setForm((prev) => ({
      ...prev,
      textFr: value,
    }));
  }, []);

  const handleTextEnChange = useCallback((value) => {
    setForm((prev) => ({
      ...prev,
      textEn: value,
    }));
  }, []);
  const handleChange = (e) => {
    const { name, value } = e.target;

    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // ================= HANDLE FILES =================

  const handleFilesChange = (e) => {
    const selectedFiles = Array.from(e.target.files);

    const mappedFiles = selectedFiles.map((file) => ({
      file,
      language: selectedLanguage,
    }));

    setFiles((prev) => [...prev, ...mappedFiles]);

    // RESET INPUT

    e.target.value = null;
  };

  // ================= REMOVE NEW FILE =================

  const removeFile = (index) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  // ================= DELETE EXISTING FILE =================

  const handleDeleteExistingFile = async (fileName) => {
    try {
      await DeleteFileAttachment(userState.token, id, fileName);

      setExistingFiles((prev) =>
        prev.filter((file) => file.fileName !== fileName),
      );

      setSnackbar({
        open: true,
        message: "File deleted successfully.",
        severity: "success",
      });
    } catch (err) {
      console.error(err);

      setSnackbar({
        open: true,
        message: err.message || "Error deleting file.",
        severity: "error",
      });
    }
  };

  // ================= CONFIRM DELETE =================

  const confirmDeleteFile = async () => {
    if (!fileToDelete) return;

    try {
      await DeleteFileAttachment(userState.token, id, fileToDelete.fileName);

      setExistingFiles((prev) =>
        prev.filter((file) => file.fileName !== fileToDelete.fileName),
      );

      setSnackbar({
        open: true,
        message: "File deleted successfully.",
        severity: "success",
      });
    } catch (err) {
      console.error(err);

      setSnackbar({
        open: true,
        message: err.message || "Error deleting file.",
        severity: "error",
      });
    } finally {
      setDeleteDialogOpen(false);

      setFileToDelete(null);
    }
  };

  // ================= DOWNLOAD FILE =================

  const handleDownloadFile = async (fileName) => {
    try {
      const blob = await DownloadAttachment(userState.token, fileName);

      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");

      link.href = url;

      link.download = fileName;

      document.body.appendChild(link);

      link.click();

      link.remove();

      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);

      setSnackbar({
        open: true,
        message: "Error downloading file",
        severity: "error",
      });
    }
  };

  // ================= CLOSE SNACKBAR =================

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({
      ...prev,
      open: false,
    }));
  };

  // ================= VALIDATION =================

  const validateForm = () => {
    if (
      !form.titleFr.trim() ||
      !form.titleEn.trim() ||
      !form.textFr.trim() ||
      !form.textEn.trim()
    ) {
      setSnackbar({
        open: true,
        message: "All fields are required.",
        severity: "error",
      });

      return false;
    }

    return true;
  };

  // ================= SUBMIT =================

  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      setLoading(true);

      const payload = {
        titleFr: form.titleFr,
        titleEn: form.titleEn,
        textFr: form.textFr,
        textEn: form.textEn,
        // FILES
        files: files.map((x) => ({
          file: x.file,
          language: x.language,
        })),
      };

      console.log("PAYLOAD:", payload);

      let result;

      // ================= UPDATE =================

      if (isEdit) {
        result = await updateArticle(token || userState.token, id, payload);
      }

      // ================= CREATE =================
      else {
        result = await createArticle(token || userState.token, payload);
      }

      setSnackbar({
        open: true,

        message:
          result.messageEn ||
          result.message ||
          (isEdit
            ? "Article updated successfully."
            : "Article created successfully."),

        severity: "success",
      });

      setTimeout(() => {
        navigate("/ArticleManagement");
      }, 1500);
    } catch (err) {
      console.error(err);

      setSnackbar({
        open: true,

        message:
          err.message ||
          (isEdit ? "Error updating article." : "Error creating article."),

        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  // ================= QUILL TOOLBAR =================

  const modules = {
    toolbar: [
      [
        {
          header: [1, 2, 3, false],
        },
      ],

      [{ font: [] }],

      [{ size: [] }],

      ["bold", "italic", "underline", "strike"],

      [{ color: [] }, { background: [] }],

      [{ align: [] }],

      [{ list: "ordered" }, { list: "bullet" }],

      ["blockquote", "code-block"],

      ["link", "image", "video"],

      ["clean"],
    ],
  };

  return (
    <Box>
      {/* ================= FRANCAIS ================= */}

      <Typography variant="h6" fontWeight={700} mb={2}>
        {t("memoHebdo.articleForm.languages.french")}
      </Typography>

      <TextField
        fullWidth
        label={t("memoHebdo.articleForm.fields.titleFr")}
        name="titleFr"
        value={form.titleFr}
        onChange={handleChange}
        sx={{ mb: 3 }}
      />

      <Typography fontWeight={600} mb={1}>
        {t("memoHebdo.articleForm.fields.contentFr")}
      </Typography>

      <Box sx={{ mb: 5 }}>
        <ReactQuill
          theme="snow"
          value={form.textFr || ""}
          onChange={handleTextFrChange}
          modules={modules}
          style={{
            height: 300,
            marginBottom: 50,
          }}
        />
      </Box>

      <Divider sx={{ my: 5 }} />

      {/* ================= ENGLISH ================= */}

      <Typography variant="h6" fontWeight={700} mb={2}>
        {t("memoHebdo.articleForm.languages.english")}
      </Typography>

      <TextField
        fullWidth
        label={t("memoHebdo.articleForm.fields.titleEn")}
        name="titleEn"
        value={form.titleEn}
        onChange={handleChange}
        sx={{ mb: 3 }}
      />

      <Typography fontWeight={600} mb={1}>
        {t("memoHebdo.articleForm.fields.contentEn")}
      </Typography>

      <Box sx={{ mb: 5 }}>
        <ReactQuill
          theme="snow"
          value={form.textEn || ""}
          onChange={handleTextEnChange}
          modules={modules}
          style={{
            height: 300,
            marginBottom: 50,
          }}
        />
      </Box>

      {/* ================= ATTACHMENTS ================= */}

      <Divider sx={{ my: 5 }} />

      <Typography variant="h6" fontWeight={700} mb={2}>
        {t("memoHebdo.articleForm.attachments.title")}
      </Typography>

      {/* ================= UPLOAD SECTION ================= */}

      <Typography variant="body2" color="text.secondary" mb={3}>
        {isEdit
          ? t("memoHebdo.articleForm.attachments.editDescription")
          : t("memoHebdo.articleForm.attachments.createDescription")}
      </Typography>

      {/* ================= UPLOAD AREA ================= */}

      <Box
        sx={{
          border: "2px dashed #1976d2",
          borderRadius: "16px",
          p: 4,
          textAlign: "center",
          background: "#f8fbff",
          mb: 3,
        }}
      >
        <CloudUploadIcon
          sx={{
            fontSize: 50,
            color: "#1976d2",
            mb: 1,
          }}
        />

        <Typography fontWeight={700} mb={1}>
          {t("memoHebdo.articleForm.attachments.clickUpload")}
        </Typography>

        {/* ================= BUTTON ================= */}

        <Button
          variant="contained"
          onClick={() => setLanguageDialogOpen(true)}
          sx={{
            borderRadius: "12px",
            textTransform: "none",
            fontWeight: 700,
          }}
        >
          {t("memoHebdo.articleForm.attachments.selectFiles")}
        </Button>

        {/* ================= HIDDEN INPUT ================= */}

        <input
          hidden
          multiple
          type="file"
          ref={fileInputRef}
          onChange={handleFilesChange}
        />
      </Box>

      {/* ================= NEW FILES ================= */}

      {files.length > 0 && (
        <>
          <Typography fontWeight={700} mb={2}>
            {isEdit
              ? t("memoHebdo.articleForm.attachments.newFiles")
              : t("memoHebdo.articleForm.attachments.selectedFiles")}
          </Typography>

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
              mb: 4,
            }}
          >
            {files.map((file, index) => (
              <Box
                key={index}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  p: 2,
                  border: "1px solid #e0e0e0",
                  borderRadius: "14px",
                  background: "#f8fbff",
                }}
              >
                {/* ================= LEFT ================= */}

                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                  }}
                >
                  <InsertDriveFileIcon color="primary" />

                  <Box>
                    <Typography fontWeight={600}>{file.file.name}</Typography>

                    {/* ================= LANGUAGE ================= */}

                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        mt: 0.5,
                      }}
                    >
                      <LanguageIcon
                        sx={{
                          fontSize: 14,
                          color: "#1976d2",
                        }}
                      />

                      <Typography
                        variant="caption"
                        sx={{
                          color: "#1976d2",
                          fontWeight: 700,
                        }}
                      >
                        {file.language === "FR"
                          ? t("memoHebdo.articleForm.languages.french")
                          : file.language === "EN"
                            ? t("memoHebdo.articleForm.languages.english")
                            : t("memoHebdo.articleForm.languages.both")}
                      </Typography>
                    </Box>

                    <Typography variant="caption" color="text.secondary">
                      {(file.file.size / 1024 / 1024).toFixed(2)} MB
                    </Typography>
                  </Box>
                </Box>

                {/* ================= RIGHT ================= */}

                <Button
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={() => removeFile(index)}
                >
                  {t("memoHebdo.articleForm.buttons.remove")}
                </Button>
              </Box>
            ))}
          </Box>
        </>
      )}

      {/* ================= EXISTING FILES ================= */}

      {isEdit && (
        <>
          <Typography variant="body2" color="text.secondary" mb={3}>
            {t("memoHebdo.articleForm.attachments.associatedFiles")}
          </Typography>

          {/* ================= EMPTY STATE ================= */}

          {(!existingFiles || existingFiles.length === 0) && (
            <Box
              sx={{
                border: "1px dashed #d0d7de",
                borderRadius: "16px",
                p: 5,
                textAlign: "center",
                background: "linear-gradient(180deg,#fafafa,#f5f7fa)",
              }}
            >
              <InsertDriveFileIcon
                sx={{
                  fontSize: 52,
                  color: "#b0b7c3",
                  mb: 1.5,
                }}
              />

              <Typography fontWeight={700} fontSize={16} mb={1}>
                {t("memoHebdo.articleForm.attachments.noAttachments")}
              </Typography>

              <Typography variant="body2" color="text.secondary">
                {t(
                  "memoHebdo.articleForm.attachments.noAttachmentsDescription",
                )}
              </Typography>
            </Box>
          )}

          {/* ================= FILE LIST ================= */}

          {existingFiles && existingFiles.length > 0 && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 2,
              }}
            >
              {existingFiles.map((file, index) => (
                <Box
                  key={index}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    p: 2,
                    border: "1px solid #e0e0e0",
                    borderRadius: "14px",
                    background: "white",
                    transition: "0.2s ease",

                    "&:hover": {
                      boxShadow: "0 4px 14px rgba(0,0,0,0.08)",
                    },
                  }}
                >
                  {/* ================= LEFT ================= */}

                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                    }}
                  >
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: "12px",
                        background: "#eef4ff",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <InsertDriveFileIcon color="primary" />
                    </Box>

                    <Box>
                      <Typography fontWeight={700}>{file.fileName}</Typography>

                      <Typography variant="caption" color="text.secondary">
                        {t("memoHebdo.articleForm.attachments.attachedFile")}
                      </Typography>
                    </Box>
                  </Box>

                  {/* ================= RIGHT ================= */}

                  <Box
                    sx={{
                      display: "flex",
                      gap: 1,
                    }}
                  >
                    {/* ================= DELETE ================= */}

                    <Button
                      variant="outlined"
                      color="error"
                      startIcon={<DeleteIcon />}
                      onClick={() => {
                        setFileToDelete(file);

                        setDeleteDialogOpen(true);
                      }}
                      sx={{
                        borderRadius: "10px",
                        textTransform: "none",
                        fontWeight: 700,
                      }}
                    >
                      {t("memoHebdo.articleForm.buttons.delete")}
                    </Button>

                    {/* ================= DOWNLOAD ================= */}

                    <Button
                      variant="contained"
                      startIcon={<DownloadIcon />}
                      onClick={() => handleDownloadFile(file.fileName)}
                      sx={{
                        borderRadius: "10px",
                        textTransform: "none",
                        fontWeight: 700,
                      }}
                    >
                      {t("memoHebdo.articleForm.buttons.download")}
                    </Button>
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </>
      )}

      {/* ================= ACTIONS ================= */}

      <Box
        sx={{
          display: "flex",
          gap: 2,
          mt: 5,
        }}
      >
        <Button variant="contained" onClick={handleSubmit} disabled={loading}>
          {loading ? (
            <CircularProgress size={22} color="inherit" />
          ) : isEdit ? (
            t("memoHebdo.articleForm.buttons.update")
          ) : (
            t("memoHebdo.articleForm.buttons.create")
          )}
        </Button>

        <Button
          variant="outlined"
          onClick={() => navigate("/ArticleManagement")}
        >
          {t("memoHebdo.articleForm.buttons.cancel")}
        </Button>
      </Box>

      {/* ================= LANGUAGE DIALOG ================= */}

      <Dialog
        open={languageDialogOpen}
        onClose={() => setLanguageDialogOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: 5,
            width: 420,
            p: 2,
          },
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: 800,
            textAlign: "center",
            fontSize: 22,
          }}
        >
          {t("memoHebdo.articleForm.dialogs.selectLanguage.title")}
        </DialogTitle>

        <DialogContent>
          <Typography
            variant="body2"
            color="text.secondary"
            textAlign="center"
            mb={4}
          >
            {t("memoHebdo.articleForm.dialogs.selectLanguage.description")}
          </Typography>

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
            }}
          >
            {/* ================= FR ================= */}

            <Button
              variant="outlined"
              startIcon={<TranslateIcon />}
              onClick={() => {
                setSelectedLanguage("FR");
                setLanguageDialogOpen(false);

                setTimeout(() => {
                  fileInputRef.current?.click();
                }, 100);
              }}
              sx={{
                borderRadius: 3,
                py: 1.6,
                px: 3,
                textTransform: "none",
                fontWeight: 700,
                fontSize: "0.95rem",
                borderColor: "#E0E0E0",
                color: "#1F2937",
                backgroundColor: "#FAFAFA",
                transition: "all 0.25s ease",
                justifyContent: "flex-start",

                "&:hover": {
                  backgroundColor: "#F3F4F6",
                  borderColor: "#6366F1",
                  transform: "translateY(-2px)",
                  boxShadow: "0 6px 18px rgba(99, 102, 241, 0.15)",
                },
              }}
            >
              {t("memoHebdo.articleForm.languages.french")}
            </Button>

            {/* ================= EN ================= */}

            <Button
              variant="outlined"
              startIcon={<LanguageIcon />}
              onClick={() => {
                setSelectedLanguage("EN");
                setLanguageDialogOpen(false);

                setTimeout(() => {
                  fileInputRef.current?.click();
                }, 100);
              }}
              sx={{
                borderRadius: 3,
                py: 1.6,
                px: 3,
                textTransform: "none",
                fontWeight: 700,
                fontSize: "0.95rem",
                borderColor: "#E0E0E0",
                color: "#1F2937",
                backgroundColor: "#FAFAFA",
                transition: "all 0.25s ease",
                justifyContent: "flex-start",

                "&:hover": {
                  backgroundColor: "#F3F4F6",
                  borderColor: "#10B981",
                  transform: "translateY(-2px)",
                  boxShadow: "0 6px 18px rgba(16, 185, 129, 0.15)",
                },
              }}
            >
              {t("memoHebdo.articleForm.languages.english")}
            </Button>

            {/* ================= BOTH ================= */}

            <Button
              variant="contained"
              onClick={() => {
                setSelectedLanguage("BOTH");

                setLanguageDialogOpen(false);

                setTimeout(() => {
                  fileInputRef.current?.click();
                }, 100);
              }}
              sx={{
                display: "none",
                borderRadius: 3,
                py: 1.5,
                textTransform: "none",
                fontWeight: 700,
              }}
            >
              {t("memoHebdo.articleForm.languages.both")}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      {/* ================= DELETE FILE DIALOG ================= */}

      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: 5,
            width: 420,
            p: 3,
            textAlign: "center",
            background: "#fff",
          },
        }}
      >
        {/* ================= ICON ================= */}

        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            mb: 1,
          }}
        >
          <WarningAmberRoundedIcon
            sx={{
              fontSize: 60,
              color: "#ff9800",
              background: "#fff3e0",
              borderRadius: "50%",
              p: 1.5,
            }}
          />
        </Box>

        {/* ================= TITLE ================= */}

        <DialogTitle
          sx={{
            fontWeight: 800,
            fontSize: 20,
          }}
        >
          {t("memoHebdo.articleForm.dialogs.deleteFile.title")}
        </DialogTitle>

        {/* ================= TEXT ================= */}

        <DialogContent>
          <DialogContentText
            sx={{
              fontSize: 14,
              color: "#666",
              mt: 1,
            }}
          >
            This action is irreversible. The file{" "}
            <strong>“{fileToDelete?.fileName}”</strong> will be permanently
            deleted.
          </DialogContentText>
        </DialogContent>

        {/* ================= ACTIONS ================= */}

        <DialogActions
          sx={{
            justifyContent: "center",
            gap: 2,
            mt: 2,
          }}
        >
          {/* ================= CANCEL ================= */}

          <Button
            onClick={() => setDeleteDialogOpen(false)}
            variant="outlined"
            sx={{
              borderRadius: 3,
              px: 3,
              textTransform: "none",
              borderColor: "#ccc",
              color: "#555",

              "&:hover": {
                borderColor: "#999",
                background: "#f5f5f5",
              },
            }}
          >
            {t("memoHebdo.articleForm.buttons.cancel")}
          </Button>

          {/* ================= DELETE ================= */}

          <Button
            onClick={confirmDeleteFile}
            variant="contained"
            color="error"
            sx={{
              borderRadius: 3,
              px: 3,
              textTransform: "none",
              fontWeight: 600,
              boxShadow: "0 6px 16px rgba(255,0,0,0.25)",

              "&:hover": {
                boxShadow: "0 8px 20px rgba(255,0,0,0.35)",
              },
            }}
          >
            {t("memoHebdo.articleForm.buttons.delete")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ================= SNACKBAR ================= */}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{
            width: "100%",
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
export default ArticleForm;
